import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaInstagram, FaFacebook, FaPhone, FaEnvelope } from 'react-icons/fa';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: ${({ theme }) => theme.transitions.medium};
`;

const TopBar = styled.div`
  background-color: ${({ theme }) => theme.colors.secondary};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.small};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const TopBarInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const TopBarInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  
  svg {
    font-size: 0.9rem;
  }
  
  a {
    color: ${({ theme }) => theme.colors.white};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TopBarSocial = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  a {
    color: ${({ theme }) => theme.colors.white};
    transition: ${({ theme }) => theme.transitions.short};
    
    &:hover {
      color: ${({ theme }) => theme.colors.light};
      transform: translateY(-2px);
    }
  }
`;

const MainHeader = styled.div`
  background-color: ${({ theme, scrolled }) => scrolled ? theme.colors.white : 'rgba(62, 23, 37, 0.85)'};
  backdrop-filter: ${({ scrolled }) => scrolled ? 'none' : 'blur(8px)'};
  padding: ${({ theme, scrolled }) => scrolled ? theme.spacing.xs : theme.spacing.sm} 0;
  margin-bottom: 40px;
  transition: ${({ theme }) => theme.transitions.medium};
  box-shadow: ${({ theme, scrolled }) => scrolled ? theme.shadows.small : '0 4px 12px rgba(0, 0, 0, 0.15)'};
  position: relative;
  height: ${({ isMobile }) => isMobile ? '60px' : 'auto'};
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ scrolled }) => scrolled ? 'none' : 'radial-gradient(circle at 50% -20%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)'};
    pointer-events: none;
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  position: relative;
  z-index: 5;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.medium};
  position: relative;
  z-index: 5;
  transform: ${({ scrolled }) => scrolled ? 'scale(0.9)' : 'scale(1)'};
  
  &:hover {
    transform: ${({ scrolled }) => scrolled ? 'scale(0.95)' : 'scale(1.05)'};
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: ${({ scrolled }) => scrolled ? '30px' : '40px'};
    width: ${({ scrolled }) => scrolled ? '100px' : '120px'};
    height: ${({ scrolled }) => scrolled ? '100px' : '120px'};
    background: radial-gradient(circle, rgba(255,192,203,0.3) 0%, rgba(255,192,203,0) 70%);
    border-radius: 50%;
    transform: translateY(-50%);
    z-index: -1;
    transition: ${({ theme }) => theme.transitions.medium};
  }
`;

const LogoImage = styled.img`
  height: ${({ scrolled }) => scrolled ? '60px' : '80px'};
  width: ${({ scrolled }) => scrolled ? '60px' : '80px'};
  border-radius: 50%;
  object-fit: contain;
  transition: ${({ theme }) => theme.transitions.medium};
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2));
  background-color: transparent;
  padding: 0;
`;

const LogoText = styled.div`
  font-family: ${({ theme }) => theme.fonts.main};
  font-weight: 700;
  transition: ${({ theme }) => theme.transitions.medium};
  margin-left: 20px;
  
  .brand-name {
    font-size: ${({ scrolled }) => scrolled ? '1.2rem' : '1.5rem'};
    color: ${({ theme, scrolled }) => scrolled ? theme.colors.secondary : theme.colors.white};
    margin-bottom: 2px;
    letter-spacing: 1px;
    font-weight: 800;
  }
  
  .tagline {
    font-size: ${({ scrolled }) => scrolled ? '0.7rem' : '0.8rem'};
    color: ${({ theme, scrolled }) => scrolled ? theme.colors.primaryDark : theme.colors.light};
    font-weight: 600;
    letter-spacing: 2px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    .tagline {
      display: none;
    }
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavItem = styled(NavLink)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  color: ${({ theme, scrolled }) => scrolled ? theme.colors.dark : theme.colors.white};
  font-weight: 700;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: ${({ theme }) => theme.transitions.medium};
  letter-spacing: 0.5px;
  font-size: 1.1rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transition: ${({ theme }) => theme.transitions.medium};
    border-radius: 3px;
  }
  
  &:hover, &.active {
    color: ${({ theme, scrolled }) => scrolled ? theme.colors.primaryDark : theme.colors.primary};
    background-color: ${({ theme, scrolled }) => scrolled ? 'rgba(255, 105, 180, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
    
    &:after {
      width: 70%;
    }
  }
  
  &.active {
    font-weight: 800;
    color:rgb(250, 131, 218) !important;
    background-color: ${({ scrolled }) => scrolled ? 'rgba(219, 112, 147, 0.2)' : 'rgba(255, 255, 255, 0.25)'};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &:after {
      width: 90%;
      height: 4px;
      background-color: rgb(250, 131, 218);
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme, scrolled }) => scrolled ? theme.colors.dark : theme.colors.white};
  text-shadow: ${({ scrolled }) => scrolled ? 'none' : '0px 1px 3px rgba(0, 0, 0, 0.5)'};
  font-size: ${({ theme }) => theme.fontSizes.large};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    background-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const BookNowButton = styled(Link)`
  background-color: ${({ theme, scrolled }) => scrolled ? theme.colors.primaryDark : 'rgba(255, 255, 255, 0.2)'};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.short};
  text-decoration: none;
  letter-spacing: 1px;
  text-transform: uppercase;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    color: ${({ theme }) => theme.colors.white};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme, scrolled }) => scrolled ? theme.colors.dark : theme.colors.white};
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenuContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 300px;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.large};
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const MobileLogo = styled.div`
  display: flex;
  align-items: center;
  
  img {
    height: 40px;
    width: 40px;
    border-radius: 50%;
    object-fit: contain;
  }
`;

const MobileSocial = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: center;
  
  a {
    color: ${({ theme }) => theme.colors.dark};
    font-size: 1.1rem;
    transition: ${({ theme }) => theme.transitions.short};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      transform: translateY(-2px);
    }
  }
`;

const MobileNavLinks = styled.nav`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const MobileNavItem = styled(NavLink)`
  padding: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.dark};
  font-weight: 600;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &.active {
    color: #ffffff;
    font-weight: 700;
    background-color: rgba(255, 105, 180, 0.1);
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.light};
  }
`;

const MobileContactInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  h4 {
    font-size: 0.9rem;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  .contact-item {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.dark};
    text-decoration: none;
    font-size: 0.9rem;
    
    svg {
      color: ${({ theme }) => theme.colors.primary};
      font-size: 0.9rem;
    }
  }
`;

const MobileFooter = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.light};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.xlarge};
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: rgba(255, 192, 203, 0.2);
    color: ${({ theme }) => theme.colors.secondary};
    transform: rotate(90deg);
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const logoPath = process.env.PUBLIC_URL + "/assets/images/logo.png"; // Path to the logo in public folder
  const logoDarkPath = process.env.PUBLIC_URL + "/assets/images/logo-dark.png"; // Path to the dark logo for white header
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <HeaderContainer scrolled={scrolled}>
      <TopBar>
        <TopBarContent>
          <TopBarInfo>
            <TopBarInfoItem>
              <FaPhone />
              <a href="tel:+918360931465">+91 8360931465</a>
            </TopBarInfoItem>
            <TopBarInfoItem>
              <FaEnvelope />
              <a href="mailto:skmakeovers24@gmail.com">skmakeovers24@gmail.com</a>
            </TopBarInfoItem>
          </TopBarInfo>
          <TopBarSocial>
            <a href="https://www.instagram.com/simrankaurmakeovers_" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </a>
            <a href="https://www.facebook.com/simrankaurmakeoverspage" target="_blank" rel="noopener noreferrer">
              <FaFacebook />
            </a>
          </TopBarSocial>
        </TopBarContent>
      </TopBar>
      
      <MainHeader scrolled={scrolled} isMobile={isMobile}>
        <NavContainer>
          <LogoContainer to="/" scrolled={scrolled}>
            <LogoImage 
              src={scrolled ? logoDarkPath : logoPath} 
              alt="Simran Kaur Makeovers" 
              scrolled={scrolled} 
            />
            {!isMobile && (
              <LogoText scrolled={scrolled}>
                <div className="brand-name">Simran Kaur Makeovers</div>
                <div className="tagline">PROFESSIONAL MAKEUP ARTIST</div>
              </LogoText>
            )}
          </LogoContainer>
          
          <NavLinks>
            <NavItem to="/" scrolled={scrolled}>Home</NavItem>
            <NavItem to="/about" scrolled={scrolled}>About</NavItem>
            <NavItem to="/services" scrolled={scrolled}>Services</NavItem>
            <NavItem to="/portfolio" scrolled={scrolled}>Portfolio</NavItem>
            <NavItem to="/academy" scrolled={scrolled}>Academy</NavItem>
            <NavItem to="/contact" scrolled={scrolled}>Contact</NavItem>
          </NavLinks>
          
          <NavActions>
            <BookNowButton to="/booking" scrolled={scrolled}>
              Book Now
            </BookNowButton>
            {/* Mobile menu icon commented out to check which looks better
            <MobileMenuButton onClick={toggleMobileMenu} scrolled={scrolled}>
              <FaBars />
            </MobileMenuButton>
            */}
          </NavActions>
        </NavContainer>
      </MainHeader>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMobileMenu}
            />
            <MobileMenuContainer
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <MobileHeader>
                <MobileLogo>
                  <img src={logoDarkPath} alt="Simran Kaur Makeovers" />
                </MobileLogo>
                <CloseButton onClick={toggleMobileMenu}>
                  <FaTimes />
                </CloseButton>
              </MobileHeader>
              
              <MobileNavLinks>
                <MobileNavItem to="/" onClick={toggleMobileMenu}>Home</MobileNavItem>
                <MobileNavItem to="/about" onClick={toggleMobileMenu}>About</MobileNavItem>
                <MobileNavItem to="/services" onClick={toggleMobileMenu}>Services</MobileNavItem>
                <MobileNavItem to="/portfolio" onClick={toggleMobileMenu}>Portfolio</MobileNavItem>
                <MobileNavItem to="/academy" onClick={toggleMobileMenu}>Academy</MobileNavItem>
                <MobileNavItem to="/contact" onClick={toggleMobileMenu}>Contact</MobileNavItem>
              </MobileNavLinks>
              
              <MobileContactInfo>
                <h4>Contact Us</h4>
                <a className="contact-item" href="tel:+918360931465">
                  <FaPhone />
                  +91 8360931465
                </a>
                <a className="contact-item" href="mailto:skmakeovers24@gmail.com">
                  <FaEnvelope />
                  skmakeovers24@gmail.com
                </a>
              </MobileContactInfo>

              <MobileSocial>
                <a href="https://www.instagram.com/simrankaurmakeovers_" target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </a>
                <a href="https://www.facebook.com/simrankaurmakeoverspage" target="_blank" rel="noopener noreferrer">
                  <FaFacebook />
                </a>
              </MobileSocial>
            </MobileMenuContainer>
          </>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header;
